import { Box, Divider, Popover, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useMemo, useRef, useState } from 'react';
import CheckBoxV4 from '../../components/CheckBoxV4';
import DropdownSvg from '../../utility/DropdownSvg';
import { HoverTaxTooltip } from '../../utility/helpers/commonFunction';
import { NumericEngine } from '../../utility/helpers/numericEngine';
import {
    buildLineDefaultTaxes,
    DOCUMENT_TAX_TYPE,
    formatTaxPercent,
    getLineTaxAccountDefault,
    getLineTaxStatus,
    getShopTaxRules,
    isFloridaLabourOnlySuppressed,
    isLineKindDiscountEligible,
    isLineTaxCustomChanged,
    LINE_TAX_KIND,
    lineNetAmount,
    PROVINCE_TAX_GROUP,
    RATE_KEY,
} from '../../utility/helpers/taxCalculation';
import { useTaxDecimalPrecision } from '../../utility/hooks/useTaxDecimalPrecision';

/**
 * LineTaxCell — shared Tax-column cell for per-line-item taxes (new multi-tax version only).
 * Renders a T / P/T / N/T chip. Hover shows a per-tax detail card; click (when editable)
 * opens a 3-state edit popover. Used in invoice/estimate create + detail pages.
 */

const STATUS_META = {
    T: { label: 'T', bg: '#EBEBEB', color: '#848484', word: 'Taxable' },
    'P/T': { label: 'P/T', bg: '#E8F0FE', color: '#006CDD', word: 'Partial Tax' },
    'N/T': { label: 'N/T', bg: '#E8F0FE', color: '#006CDD', word: 'Non-taxable' },
};

const formatCurrency = (n) => (Number(n) > 0 ? NumericEngine.format(n, { formatCurrency: true, trailingZeros: true }) : '$0.00');

const cellSx = {
    border: 0,
    fontSize: '14px',
    lineHeight: '18px',
    whiteSpace: 'nowrap',

    padding: '0',

    '&:first-of-type': {
        paddingLeft: '12px',
    },

    '&:nth-of-type(2)': {
        paddingLeft: '12px',
    },

    '&:last-of-type': {
        paddingLeft: '12px',
        paddingRight: '12px',
    },
};

const LineTaxCell = ({
    taxesBreakdown,
    lineAmount = 0,
    documentType = DOCUMENT_TAX_TYPE.INVOICE,
    shopTaxesData,
    kind,
    editable = false,
    onChange,
    globalTaxesBreakdown = null,
    // Document discount + subtotal, so this line's TAXABLE amount (its share of the discount
    // removed) can be derived exactly the way the payment summary derives it. Omit them and the
    // taxable amount collapses to the gross — correct for fees, which are discount-exempt.
    discount = 0,
    subTotal = 0,
    // Province tax group (see PROVINCE_TAX_GROUP) + whether the WHOLE document carries a taxable part
    // or service package. Drive the Florida labour-only rule and the province-specific N/T tooltip.
    // Create passes editor-derived values; detail pages pass record-derived ones.
    provinceTaxGroup = PROVINCE_TAX_GROUP.DEFAULT,
    documentHasPartsOrPackages = false,
}) => {
    // Each document has its own rate field on a tax rule — never assume the invoice's.
    const rateKey = RATE_KEY[documentType] || RATE_KEY[DOCUMENT_TAX_TYPE.INVOICE];
    const precision = useTaxDecimalPrecision();
    const [anchorEl, setAnchorEl] = useState(null);
    const closeTimerRef = useRef(null);

    // Florida labour-only: this labour line's tax is suppressed while the whole document is
    // labour-only. Line-level flag; whether it removes a given tax is decided per-rule below
    // (only appliesToLabour=true rules), so appliesToLabour=false rules stay soft.
    const flLabourSuppressed = kind === LINE_TAX_KIND.LABOUR && isFloridaLabourOnlySuppressed(provinceTaxGroup, documentHasPartsOrPackages);

    // Resolve display rows: line's own breakdown (fallback to shop defaults), rate/name merged
    // from shop rules. `effective` = global master gate AND the line's own applyTax AND not suppressed.
    console.log('taxesBreakdown', taxesBreakdown);
    const rows = useMemo(() => {
        const rules = getShopTaxRules(shopTaxesData);
        const ruleMap = new Map(rules.map((r) => [r._id, r]));
        const globalMap = new Map((globalTaxesBreakdown || []).map((r) => [r.taxId, r]));
        const base = Array.isArray(taxesBreakdown) && taxesBreakdown.length ? taxesBreakdown : buildLineDefaultTaxes(shopTaxesData, kind);
        return (base || []).map((t) => {
            const rule = ruleMap.get(t.taxId);
            const g = globalMap.get(t.taxId);
            const globalOn = g ? g.applyTax !== false : true;
            const own = !!t.applyTax;
            if (t.taxName === 'PST') {
                console.log('🚀 ~ LineTaxCell ~ t:', !!t.applyTax, t);
            }
            // Account-level default for this line kind (for labour, this IS appliesToLabour).
            const accountDefault = getLineTaxAccountDefault(g ?? rule, kind);
            // Florida removes labour tax only from rules that would otherwise tax labour.
            const suppressed = flLabourSuppressed && accountDefault === true;
            return {
                taxId: t.taxId,
                // Name + rate follow the current global row (user-editable) first, then the line/shop rule.
                taxName: t.name ?? t.taxName ?? g?.name ?? rule?.taxName ?? '',
                applyTax: own,
                globalOn,
                accountDefault,
                suppressed,
                // Stored on the saved line breakdown. On detail pages this is the ONLY appliesToLabour
                // signal we trust — customChange=false + applyTax=false means the tax was off by the
                // account default at save time; customChange=true means the user overrode it.
                customChange: !!t.customChange,
                effective: own && !suppressed,
                rate: Number(g?.[rateKey] ?? t[rateKey] ?? rule?.[rateKey]) || 0,
                // Stored per-tax amount from the GET response (used for exact display on read pages).
                taxAmount: t.taxAmount != null ? Number(t.taxAmount) : null,
            };
        });
    }, [taxesBreakdown, globalTaxesBreakdown, shopTaxesData, kind, rateKey, flLabourSuppressed]);

    const priceNum = Number(lineAmount) || 0;

    // An in-progress part row with no amount yet (typically the unmanaged "Search from inventory"
    // row before a price is entered). It is not a taxable line — the summary already excludes it
    // (isRealPart needs amount > 0) — so the chip must not claim otherwise. Show N/T + a prompt to
    // finish the row, so the inline chip and the summary agree.
    //
    // DISABLED to match buildTaxableLines/buildTaxableLinesFromRecord, where the $0-line guards are
    // commented out with the same `❌` marker: those emit $0 lines as taxable, so forcing the chip to
    // N/T here made the chip and the summary disagree. Re-enable this line and the `❌` guards in
    // taxCalculation.js TOGETHER — one without the other reintroduces the mismatch.
    const isIncompletePart = false;
    // const isIncompletePart = kind === LINE_TAX_KIND.PARTS && priceNum <= 0; // ❌ drops $0 parts

    // Florida labour-only, and every applicable rule is suppressed → the chip is locked N/T (Case A).
    // Anything else stays editable: the account default decides the start, the user may override.
    const allSuppressed = rows.length > 0 && rows.every((r) => r.suppressed);
    const interactive = editable && !isIncompletePart && !allSuppressed;

    const isLabour = kind === LINE_TAX_KIND.LABOUR;
    const inLabourToolTipGroup = provinceTaxGroup === PROVINCE_TAX_GROUP.FLORIDA || provinceTaxGroup === PROVINCE_TAX_GROUP.GROUP_B;

    // ---- Which N/T explanation to show. Two sources, deliberately:
    // CREATE (editable): live account settings. appliesToLabour comes from the current shop rule.
    // DETAIL (read-only): the saved inline breakdown ONLY — never the live rule, so a rule the shop
    //   later changed can't relabel a historical document. appliesToLabour is inferred from what was
    //   stored: applyTax=false + customChange=false ⇒ it was the account default (labour billed
    //   separately); customChange=true ⇒ the user overrode it (plain "no tax").
    // Florida Case A (labour-only document) is decided the same way in both: FL + no parts/packages.
    const flLabourOnlyDoc = isLabour && provinceTaxGroup === PROVINCE_TAX_GROUP.FLORIDA && !documentHasPartsOrPackages;

    // Create-time "labour separated" — every rule's account default is off (appliesToLabour=false).
    const labourSeparatedLive = isLabour && inLabourToolTipGroup && rows.length > 0 && rows.every((r) => r.accountDefault === false);

    // Detail-time "labour off by account default" — trusted from the stored breakdown, not live rules.
    const labourDefaultOffStored = isLabour && rows.length > 0 && rows.every((r) => !r.applyTax && !r.customChange);

    // Case A message (both flows): Florida labour-only document. On create, only when a rule would
    // otherwise tax labour (appliesToLabour=true) — an appliesToLabour=false labour is "separated",
    // not "labour-only". On detail we don't read the live rule, so FL + labour-only doc is enough.
    const showLabourOnlyMsg = editable ? allSuppressed : flLabourOnlyDoc;
    // Case B message: labour billed separately.
    const showSeparatedMsg = editable
        ? labourSeparatedLive && !showLabourOnlyMsg
        : isLabour && inLabourToolTipGroup && labourDefaultOffStored && !showLabourOnlyMsg;

    // Incomplete part → force N/T; otherwise the normal per-tax status.
    const status = isIncompletePart ? 'N/T' : getLineTaxStatus(rows.map((r) => ({ applyTax: r.effective })));
    console.log('🚀 ~ LineTaxCell ~ rows:', rows);
    const meta = STATUS_META[status] || STATUS_META['N/T'];

    const appliedRows = isIncompletePart ? [] : rows.filter((r) => r.effective);

    // TAXABLE amount for this line: its gross minus its pro-rata share of the document discount.
    // Tax is charged on this, never on the gross. Uses the same helper as the payment summary and
    // the totals engine, so the per-line amounts below always add up to the tax row in the summary.
    // (They previously used the gross here, which overstated every chip whenever a discount existed.)
    const netNum = lineNetAmount({
        gross: priceNum,
        discount,
        subTotal,
        discountEligible: isLineKindDiscountEligible(kind),
    });

    // Read-only pages: show the exact stored per-tax amount from the GET response.
    // Live edit: recompute from taxable × rate (values change as the user types).
    // Each per-tax amount is rounded to 2dp BEFORE summing, so the rows always add up to the
    // "Total tax on this item" shown beneath them.
    const rowTaxAmt = (r) => (!editable && r.taxAmount != null ? Number(r.taxAmount) : Number(NumericEngine.format((netNum * r.rate) / 100, { precision: 2 })));
    const totalTax = appliedRows.reduce((acc, r) => Number(NumericEngine.add(String(acc), String(rowTaxAmt(r)))), 0);

    const emit = (nextRows) => {
        if (!onChange) return;
        onChange(
            nextRows.map((r) => ({
                taxId: r.taxId,
                taxName: r.taxName,
                // A Florida-suppressed labour tax is never written back as applied.
                applyTax: !r.suppressed && !!r.applyTax,
                // Diverging from the account default is what makes a line "custom" — so flipping a
                // value back to the default clears the flag on its own. A Florida-suppressed tax is a
                // system rule, not a user change, so it never counts as custom.
                customChange: r.suppressed ? false : isLineTaxCustomChanged(r.applyTax, r.accountDefault),
            }))
        );
    };

    // Suppressed taxes are never re-enabled — "Taxable (Full)" can't override the Florida rule, and
    // toggling a suppressed row is a no-op.
    const setAll = (value) => emit(rows.map((r) => ({ ...r, applyTax: r.suppressed ? false : value })));
    const toggleOne = (taxId) => emit(rows.map((r) => (r.taxId === taxId && !r.suppressed ? { ...r, applyTax: !r.applyTax } : r)));

    const handleOpen = (e) => {
        if (!interactive) return;
        e.stopPropagation();
        setAnchorEl(e.currentTarget);
    };
    const handleClose = () => {
        if (closeTimerRef.current) {
            clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
        setAnchorEl(null);
    };

    const setAllAndScheduleClose = (value) => {
        setAll(value);
        if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
        closeTimerRef.current = setTimeout(() => {
            handleClose();
        }, 1000);
    };

    const applyAll = (value, targetKey) => {
        const isSwitching = status !== targetKey;
        setAll(value);
        if (isSwitching) {
            if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
            closeTimerRef.current = setTimeout(() => {
                handleClose();
            }, 1000);
        }
    };
    // "Manually Adjusted" — the user changed THIS line's tax away from the account default. It rides
    // on the existing customChange flag, so: a payment-summary (global) uncheck never sets it, Florida
    // suppression bakes it false, and it survives an edit via the stored customChange. Suppressed and
    // incomplete rows never count.
    const manuallyAdjusted = !isIncompletePart && rows.some((r) => !r.suppressed && r.customChange);
    const manualAdjustedPillSx = {
        height: '26px',
        background: 'rgba(0, 108, 221, 0.10)',
        textAlign: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    };
    const manualAdjustedPillText = <Typography sx={{ color: '#006CDD', fontSize: 14, fontWeight: 500, lineHeight: '18px' }}>Manually Adjusted</Typography>;

    // ---- Hover detail card ----
    const hoverCard =
        appliedRows.length === 0 ? (
            <Box sx={{ p: '10px 12px', borderRadius: '8px', overflow: 'hidden' }}>
                <Typography sx={{ fontSize: '12px', color: '#212121', lineHeight: '150%' }}>
                    {/* Which N/T explanation to show:
                        0. unpriced part row → prompt to finish it (not yet a taxable line).
                        1. Florida labour-only (Case A) → labour is non-taxable until a part is added.
                        2. labour billed separately, appliesToLabour=false in FL/Group-B → separated.
                        3. anything else (manual override, or default province) → the plain message. */}
                    {isIncompletePart
                        ? 'Add an amount to see the applicable tax.'
                        : showLabourOnlyMsg
                          ? 'Labour-only repair is non taxable when no parts are used.'
                          : showSeparatedMsg
                            ? 'Labour is non-taxable when separated from parts.'
                            : 'No tax is applied to this item.'}
                </Typography>
                {manuallyAdjusted && <Box sx={{ ...manualAdjustedPillSx, mt: '8px' }}>{manualAdjustedPillText}</Box>}
            </Box>
        ) : (
            <Box sx={{ minWidth: '100%', p: '0', borderRadius: '8px', overflow: 'hidden' }}>
                <Table
                    sx={{
                        width: 'auto',
                        borderCollapse: 'separate',
                        borderSpacing: 0,
                    }}
                >
                    <TableHead>
                        <TableRow
                            sx={{
                                background: '#F8F8F8',

                                '& .MuiTableCell-root': {
                                    height: 36,
                                    paddingTop: '8px',
                                    paddingBottom: '8px',
                                    fontWeight: 600,
                                },
                            }}
                        >
                            <TableCell sx={{ ...cellSx, fontWeight: 600, color: '#212121', paddingTop: '8px', paddingBottom: '8px' }}>Tax</TableCell>
                            <TableCell sx={{ ...cellSx, fontWeight: 600, color: '#212121', paddingTop: '8px', paddingBottom: '8px' }}>Rate</TableCell>
                            {/* Taxable = this line's amount after its share of the discount — the base each
                                rate is actually applied to. Shown so Taxable × Rate = Tax Amt reads straight
                                across the row. */}
                            {/* <TableCell align="right" sx={{ ...cellSx, fontWeight: 600, color: '#212121', paddingTop: '8px', paddingBottom: '8px' }}>
                                Taxable
                            </TableCell> */}
                            <TableCell align="right" sx={{ ...cellSx, fontWeight: 600, color: '#212121', paddingTop: '8px', paddingBottom: '8px' }}>
                                Tax Amt
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {appliedRows.map((r) => (
                            <TableRow
                                key={r.taxId}
                                sx={{
                                    '& .MuiTableCell-root': {
                                        paddingTop: '10px',
                                        paddingBottom: '0px',
                                    },
                                }}
                            >
                                <TableCell sx={{ ...cellSx, color: '#212121', paddingTop: '8px' }}>{r.taxName}</TableCell>
                                <TableCell sx={{ ...cellSx, color: 'rgba(0,0,0,.6)', paddingTop: '8px' }}>{formatTaxPercent(r.rate, precision)}%</TableCell>
                                {/* <TableCell align="right" sx={{ ...cellSx, color: 'rgba(0,0,0,.6)', paddingTop: '8px' }}>
                                    {formatCurrency(netNum)}
                                </TableCell> */}
                                <TableCell align="right" sx={{ ...cellSx, color: '#212121', paddingTop: '8px' }}>
                                    {formatCurrency(rowTaxAmt(r))}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>

                <Box
                    sx={{
                        px: '12px',
                        pt: '10px',
                        pb: '10px',

                        display: 'flex',
                        flexDirection: 'column',

                        gap: '10px',
                    }}
                >
                    <Divider
                        sx={{
                            borderColor: 'rgba(0,0,0,.10)',
                        }}
                    />
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',

                            minHeight: 18,
                        }}
                    >
                        <Typography sx={{ fontWeight: 500, fontSize: 14 }}>Total Tax</Typography>

                        <Typography sx={{ fontWeight: 500, fontSize: 14 }}>{formatCurrency(totalTax)}</Typography>
                    </Box>
                    {manuallyAdjusted && <Box sx={manualAdjustedPillSx}>{manualAdjustedPillText}</Box>}
                </Box>
            </Box>
        );

    const chip = (
        <Box
            onClick={handleOpen}
            sx={{
                display: 'flex',
                width: editable ? '60px' : 'max-content',
                height: '28px',
                px: editable ? (interactive ? '8px' : '8px') : '0px',
                pr: editable ? (interactive ? '6px' : '8px') : '0px',
                py: editable ? '4px' : '0px',

                justifyContent: 'space-between',
                alignItems: 'center',
                borderRadius: editable ? '38px' : '0px',
                backgroundColor: editable ? meta.bg : 'transparent',
                boxSizing: 'border-box',
                cursor: interactive ? 'pointer' : 'default',
                userSelect: 'none',
            }}
        >
            <Typography
                sx={{
                    flex: '1 0 0',
                    color: editable ? meta.color : '#848484',
                    textAlign: 'center',
                    fontSize: '14px',
                    fontWeight: 600,
                    fontStyle: 'normal',
                    lineHeight: '143%',
                    letterSpacing: '0.17px',
                    fontFamily: 'Roboto',
                }}
            >
                {meta.label}
            </Typography>

            {interactive && (
                <Box
                    sx={{
                        width: 20,
                        height: 20,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}
                >
                    <DropdownSvg color={meta.color} />
                </Box>
            )}
        </Box>
    );

    return (
        <>
            <HoverTaxTooltip title={hoverCard} placement="top" arrow>
                <span style={{ display: 'inline-flex' }}>{chip}</span>
            </HoverTaxTooltip>

            {interactive && (
                <Popover
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    PaperProps={{
                        sx: {
                            minWidth: '160px',
                            p: '8px',
                            borderRadius: '8px',
                            border: '1px solid rgba(0,0,0,0.10)',
                            background: '#FFF',
                            boxShadow: '0px 0px 24px rgba(0,0,0,0.10)',
                            overflow: 'hidden',
                        },
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '4px',
                        }}
                    >
                        {/* 2-state header: Taxable | Non-taxable (partial tax row removed) */}
                        <Stack spacing={0.5}>
                            {[
                                {
                                    key: 'T',
                                    label: 'Taxable',
                                    suffix: `(Full)`,
                                    onClick: () => applyAll(true, 'T'),
                                },
                                {
                                    key: 'N/T',
                                    label: 'Non-taxable',
                                    suffix: '(N/T)',
                                    onClick: () => applyAll(false, 'N/T'),
                                },
                            ].map((seg) => {
                                // Only highlight when EVERY tax shares the same on/off state.
                                // status === 'P/T' (mixed) means neither row lights up.
                                const active = status === seg.key;
                                return (
                                    <Box
                                        key={seg.key}
                                        onClick={seg.onClick}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            alignSelf: 'stretch',
                                            p: '6px',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            background: active ? 'rgba(0, 108, 221, 0.10)' : '#FFF',
                                            '&:hover': {
                                                background: active ? 'rgba(0, 108, 221, 0.10)' : '#F8F8F8',
                                            },
                                        }}
                                    >
                                        <Typography sx={{ fontSize: '14px', fontWeight: 400, lineHeight: '18.2px', color: '#212121' }}>{seg.label}</Typography>
                                        <Typography sx={{ fontSize: '12px', fontWeight: 400, lineHeight: '17.16px', color: '#757575' }}>
                                            {seg.suffix}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Stack>

                        <Divider
                            sx={{
                                borderColor: 'rgba(0,0,0,0.10)',
                            }}
                        />

                        {/* Per-tax checkbox list */}
                        {rows.map((r) => (
                            <Stack
                                key={r.taxId}
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                sx={{
                                    display: 'flex',
                                    padding: '6px 6px 6px 3px',
                                    alignItems: 'center',
                                    gap: '4px',
                                    alignSelf: 'stretch',
                                    borderRadius: '4px',
                                    '&:hover': {
                                        background: '#F8F8F8',
                                    },
                                }}
                            >
                                <Stack direction="row" alignItems="center">
                                    {/* Disabled when the global row gates this tax off, OR when the
                                        Florida labour rule suppresses it (a hard, non-overridable rule). */}
                                    <CheckBoxV4
                                        checked={r.effective}
                                        disabled={r.suppressed}
                                        onChange={() => toggleOne(r.taxId)}
                                        sx={{
                                            p: 0,
                                            mr: '8px',
                                        }}
                                    />
                                    <Typography
                                        sx={{
                                            fontSize: '14px',
                                            fontWeight: 400,
                                            lineHeight: '18.2px',
                                            color: !r.suppressed ? 'inherit' : 'rgba(0,0,0,0.4)',
                                        }}
                                    >
                                        {r.taxName}
                                    </Typography>
                                </Stack>
                                <Typography
                                    sx={{
                                        fontSize: '12px',
                                        fontWeight: 400,
                                        lineHeight: '17.16px',
                                        color: '#616161',
                                    }}
                                >
                                    {formatTaxPercent(r.rate, precision)}%
                                </Typography>
                            </Stack>
                        ))}
                    </Box>
                </Popover>
            )}
        </>
    );
};

export default LineTaxCell;
